import type { Nullable } from "@core/domain/types/nullable.type";
import { DeleteDocumentUseCase } from "@core/domain/usecases/delete-document.usecase";
import { DownloadDocumentUseCase } from "@core/domain/usecases/download-document.usecase";
import { LoadLayoutStore } from "@core/presentacion/component/feedback/load-layout/load-layout.store";
import { ToastManagerStore } from "@core/presentacion/component/feedback/toast-manager/toast-manager.store";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { CCAA } from "@entity/domain/models/ccaa.model";
import { CostIVAType } from "@entity/domain/models/cost/cost-iva-type.model";
import {
    CostSubtype,
    CostSubtypeEnum,
    CostType,
    CostTypeEnum,
    EXPENSE_SUBTYPE_TABLE_LOOKUP,
} from "@entity/domain/models/cost/cost-type.model";
import {
    AdditionalExpense,
    AdditionalExpenseCreate,
} from "@entity/domain/models/cost/cost.model";
import { IRPFType } from "@entity/domain/models/cost/irpf-type.model";
import { PaymentMethodType } from "@entity/domain/models/cost/payment-method-type.model";
import {
    TransportType,
    TransportTypeEnum,
} from "@entity/domain/models/cost/transport-type.model";
import { IdentificationType } from "@entity/domain/models/identification-type.model";
import { ProjectSummary } from "@entity/domain/models/project-summary.model";
import { ServiceProvided } from "@entity/domain/models/supplier/service-provided.model";
import { GetAllCostIvaTypesUseCase } from "@entity/domain/usecases/cost/get-all-cost-iva-types.usecase";
import { GetAllCostsTypesUseCase } from "@entity/domain/usecases/cost/get-all-costs-types.usecase";
import { GetAllIrpfTypesUseCase } from "@entity/domain/usecases/cost/get-all-irpf-types.usecase";
import { GetAllPaymentMethodTypesUseCase } from "@entity/domain/usecases/cost/get-all-payment-method-types.usecase";
import { UpdateAdditionalExpensesUseCase } from "@entity/domain/usecases/cost/update-additional-expenses.usecase";
import { GetAllCCAAUseCase } from "@entity/domain/usecases/get-all-ccaa.usecase";
import { GetAllIdentificationTypesUseCase } from "@entity/domain/usecases/get-all-identification-types.usecase";
import { GetAllServiceProvidedUseCase } from "@entity/domain/usecases/supplier/get-all-service-provided.usecase";
import { GetAllProjectsUseCase } from "@project/domain/usecases/get-all-projects.usecase";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class CostFormViewModel extends BaseViewModel {
    @observable
    initialLoading: boolean = true;

    @observable
    identificationTypes: IdentificationType[] = [];

    @observable
    paymentMethodTypes: PaymentMethodType[] = [];

    @observable
    ivaTypes: CostIVAType[] = [];

    @observable
    costTypes: CostType[] = [];

    @observable
    subtypes: CostSubtype[] = [];

    @observable
    allSubtypes: CostSubtype[] = [];

    @observable
    irpfTypes: IRPFType[] = [];

    @observable
    ccaaTypes: CCAA[] = [];

    @observable
    transportTypes: TransportType[] = [];

    @observable
    projects: ProjectSummary[] = [];

    @observable
    categories: ServiceProvided[] = [];

    @observable
    showLinkCostToProjectModal: boolean = false;

    @observable
    expenseSelected: Nullable<AdditionalExpense> = null;

    constructor(
        @inject(DeleteDocumentUseCase)
        private readonly deleteDocumentUseCase: DeleteDocumentUseCase,
        @inject(DownloadDocumentUseCase)
        private readonly downloadDocumentUseCase: DownloadDocumentUseCase,
        @inject(GetAllPaymentMethodTypesUseCase)
        private readonly getAllPaymentMethodTypesUseCase: GetAllPaymentMethodTypesUseCase,
        @inject(GetAllIdentificationTypesUseCase)
        private readonly getAllIdentificationTypesUseCase: GetAllIdentificationTypesUseCase,
        @inject(GetAllProjectsUseCase)
        private readonly getAllProjectsUseCase: GetAllProjectsUseCase,
        @inject(GetAllCostIvaTypesUseCase)
        private readonly getAllCostIvaTypesUseCase: GetAllCostIvaTypesUseCase,
        @inject(GetAllCostsTypesUseCase)
        private readonly getAllCostsTypesUseCase: GetAllCostsTypesUseCase,
        @inject(GetAllIrpfTypesUseCase)
        private readonly getAllIrpfTypesUseCase: GetAllIrpfTypesUseCase,
        @inject(GetAllCCAAUseCase)
        private readonly getAllCcaaUseCase: GetAllCCAAUseCase,
        @inject(UpdateAdditionalExpensesUseCase)
        private readonly updateAdditionalExpensesUseCase: UpdateAdditionalExpensesUseCase,
        @inject(GetAllServiceProvidedUseCase)
        private readonly getAllServiceProvidedUseCase: GetAllServiceProvidedUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override async didMount(): Promise<void> {
        await super.didMount();

        await this.initViewData();
    }

    async initViewData(): Promise<void> {
        await Promise.all([
            this.getAllPaymentMethodTypes(),
            this.getIdentificationTypes(),
            this.getAllProjects(),
            this.getAllCostIvaTypes(),
            this.getAllCostsTypes(),
            this.getAllIrpfTypes(),
            this.getAllCcaaTypes(),
            this.getAllCategories(),
            this.initializeTransportTypes(),
        ]);

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    async getAllPaymentMethodTypes(): Promise<void> {
        const paymentMethodTypes =
            await this.getAllPaymentMethodTypesUseCase.execute();

        runInAction(() => {
            this.paymentMethodTypes = paymentMethodTypes;
        });
    }

    async getAllCostIvaTypes(): Promise<void> {
        const ivaTypes = await this.getAllCostIvaTypesUseCase.execute();

        runInAction(() => {
            this.ivaTypes = ivaTypes;
        });
    }

    async getAllIrpfTypes(): Promise<void> {
        const irpfTypes = await this.getAllIrpfTypesUseCase.execute();

        runInAction(() => {
            this.irpfTypes = irpfTypes;
        });
    }

    async getAllCcaaTypes(): Promise<void> {
        const ccaaTypes = await this.getAllCcaaUseCase.execute();

        if (ccaaTypes) {
            runInAction(() => {
                this.ccaaTypes = ccaaTypes;
            });
        }
    }

    async getIdentificationTypes(): Promise<void> {
        const identificationTypes =
            await this.getAllIdentificationTypesUseCase.execute();

        runInAction(() => {
            this.identificationTypes = identificationTypes;
        });
    }

    async getAllProjects(): Promise<void> {
        const projects = await this.getAllProjectsUseCase.execute();

        runInAction(() => {
            this.projects = projects;
        });
    }

    async downloadDocument(documentUrl: string): Promise<void> {
        LoadLayoutStore.start();

        if (documentUrl) {
            await this.downloadDocumentUseCase.execute(documentUrl);
        }

        LoadLayoutStore.finish();
    }

    async deleteDocument(documentId: number): Promise<boolean> {
        LoadLayoutStore.start();

        const deleted = await this.deleteDocumentUseCase.execute(documentId);

        if (deleted) {
            ToastManagerStore.success();
        }

        LoadLayoutStore.finish();

        return deleted;
    }

    // En tu método getAllCostsTypes, después de obtener los tipos de costos, carga todos los subtipos
    async getAllCostsTypes(): Promise<void> {
        const costsTypes = await this.getAllCostsTypesUseCase.execute();

        runInAction(() => {
            this.costTypes = costsTypes;

            // Carga todos los posibles subtipos cuando obtienes los tipos
            this.allSubtypes = Object.values(CostSubtypeEnum).map(
                (subtypeEnum) =>
                    new CostSubtype(
                        subtypeEnum,
                        this.getSubtypeLabel(subtypeEnum),
                    ),
            );
        });
    }

    // Añade este método helper para obtener las etiquetas de los subtipos
    getSubtypeLabel(subtypeEnum: CostSubtypeEnum): string {
        // Este mapeo debe coincidir con los valores localizados que se muestran en la UI
        switch (subtypeEnum) {
            case CostSubtypeEnum.NONE:
                return "Ninguno";
            case CostSubtypeEnum.DISPLACEMENT:
                return "Desplazamientos";
            case CostSubtypeEnum.ACCOMMODATION_MEALS:
                return "Alojamiento y dietas";
            case CostSubtypeEnum.THIRD_PARTY_ACCOUNT:
                return "Por cuenta ajena";
            case CostSubtypeEnum.THIRD_PARTY_SERVICES:
                return "Por cuenta propia";
            case CostSubtypeEnum.RENT:
                return "Alquileres";
            case CostSubtypeEnum.UTILITIES:
                return "Suministros";
            case CostSubtypeEnum.UTILITIES_CONSTRUCTION_AND_EQUIPMENT:
                return "Obras y equipamientos";
            case CostSubtypeEnum.INSURANCE:
                return "Seguros";
            case CostSubtypeEnum.CONSUMABLES:
                return "Fungibles";
            case CostSubtypeEnum.MATERIALS_FOR_ACTIVITIES:
                return "Materiales para actividades";
            case CostSubtypeEnum.TRANSPORT_OF_GOODS_AND_INMOVILITIES:
                return "Transporte de bienes e inmovilidades";
            case CostSubtypeEnum.OCCASIONAL_COLLABORATIONS:
                return "Colaboraciones ocasionales";
            case CostSubtypeEnum.CONFERENCES_LECTURES:
                return "Conferencias y charlas";
            case CostSubtypeEnum.WORK_OTHER_COMPANIES:
                return "Trabajo de otras empresas";
            case CostSubtypeEnum.VOLUNTEERING:
                return "Voluntariado";
            case CostSubtypeEnum.LIABILITY_INSURANCE:
                return "Seguro de responsabilidad";
            case CostSubtypeEnum.EXPENSES_INCURRED_BENEFICIARIES:
                return "Gastos incurridos por beneficiarios";
            case CostSubtypeEnum.SOFTWARE_LICENSES:
                return "Licencias de software";
            case CostSubtypeEnum.OTHERS_MINOR_EXPENSES:
                return "Otros gastos menores";
            case CostSubtypeEnum.FURNITURE:
                return "Mobiliario";
            case CostSubtypeEnum.COMPUTER_EQUIPMENT:
                return "Equipamiento informático";
            case CostSubtypeEnum.REAL_ESTATE:
                return "Bienes inmuebles";
            case CostSubtypeEnum.OTHERS_INVESTMENT_EXPENSES:
                return "Otros gastos de inversión";
            case CostSubtypeEnum.CONSULTING_SERVICES:
                return "Gestoría";
            case CostSubtypeEnum.AUDITING:
                return "Auditoría";
            case CostSubtypeEnum.LABOR_MANAGEMENT_LICENSES:
                return "Licencias de gestión laboral";
            case CostSubtypeEnum.QUALITY:
                return "Calidad";
            case CostSubtypeEnum.PERSONNEL:
                return "Personal";
            default:
                return CostSubtypeEnum.NONE;
        }
    }

    initializeTransportTypes(): void {
        runInAction(() => {
            this.transportTypes = Object.values(TransportTypeEnum).map(
                (transportType) =>
                    new TransportType(
                        transportType,
                        this.getTransportTypeLabel(transportType),
                    ),
            );
        });
    }

    private getTransportTypeLabel(transportType: TransportTypeEnum): string {
        switch (transportType) {
            case TransportTypeEnum.NONE:
                return "Ninguno";
            case TransportTypeEnum.CAR:
                return "Coche";
            case TransportTypeEnum.BUS:
                return "Autobús";
            case TransportTypeEnum.METRO:
                return "Metro";
            case TransportTypeEnum.TAXI:
                return "Taxi";
            case TransportTypeEnum.PLANE:
                return "Avión";
            case TransportTypeEnum.OTHER:
                return "Otro";
            default:
                return transportType;
        }
    }

    // Y luego el método updateSubtypesForType funciona correctamente
    @action
    updateSubtypesForType(selectedType: CostTypeEnum): void {
        // Si el tipo no tiene subtipos, limpiamos el array
        if (
            !EXPENSE_SUBTYPE_TABLE_LOOKUP[selectedType] ||
            EXPENSE_SUBTYPE_TABLE_LOOKUP[selectedType].length === 0
        ) {
            this.subtypes = [];
            return;
        }

        // Filtramos los subtipos disponibles para el tipo seleccionado
        this.subtypes = this.allSubtypes.filter((subtype) =>
            EXPENSE_SUBTYPE_TABLE_LOOKUP[selectedType].includes(subtype.id),
        );
    }

    @action
    closeLinkCostToProjectModal(): void {
        this.showLinkCostToProjectModal = false;
        this.expenseSelected = null;
    }

    @action
    openLinkCostToProjectModal(): void {
        this.showLinkCostToProjectModal = true;
    }

    @action
    setExpenseSelected(expense: AdditionalExpense): void {
        this.expenseSelected = expense;
    }

    async updateAdditionalExpense(
        expense: AdditionalExpenseCreate,
    ): Promise<void> {
        LoadLayoutStore.start();

        const updated =
            await this.updateAdditionalExpensesUseCase.execute(expense);

        if (updated) {
            ToastManagerStore.success();
        }

        LoadLayoutStore.finish();
    }

    async getAllCategories(): Promise<void> {
        const categories = await this.getAllServiceProvidedUseCase.execute();

        runInAction(() => {
            this.categories = categories;
        });
    }
}
